import {
  Badge,
  Box,
  Divider,
  Flex,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { PlatformRequirementDocumentVerdict } from '@client/graphql/__generated__/types';
import { FC } from 'react';
import { match, P } from 'ts-pattern';
import {
  ChecklistContractDocumentView,
  ChecklistGroupedEvaluationDocumentView,
  DOCUMENT_BADGE_TONE_STYLE,
  DocumentBadgeTone,
} from './checklistIssue.types';
import {
  ChecklistFormChip,
  ChecklistRequirementLine,
  formatCategory,
} from './checklistIssueChips';

/**
 * Verdict badge for one contract-document row.
 *
 * Driven by the recorded verdict alone, unlike `getDocumentStatusBadge`, which
 * also *infers* a clean state from `evaluationStatus === 'evaluated'` when no
 * verdict exists — hence its hedged "No mismatch" wording (BLO-13160: never
 * assert clean without evidence). This detail view only renders persisted
 * evaluation evidence, so a recorded MATCHED is real and the plain "Matched"
 * is truthful. The two look redundant but answer different questions.
 */
const getRecordedVerdictBadge = (
  verdict: PlatformRequirementDocumentVerdict | null | undefined
): { label: string; tone: DocumentBadgeTone } =>
  match(verdict)
    .with(PlatformRequirementDocumentVerdict.MISMATCHED, () => ({
      label: 'Mismatch',
      tone: 'error' as const,
    }))
    .with(PlatformRequirementDocumentVerdict.MATCHED, () => ({
      label: 'Matched',
      tone: 'success' as const,
    }))
    .with(PlatformRequirementDocumentVerdict.UNRESOLVED, () => ({
      label: 'Unresolved',
      tone: 'neutral' as const,
    }))
    .with(PlatformRequirementDocumentVerdict.UNCONFIGURED, () => ({
      label: 'No requirement',
      tone: 'neutral' as const,
    }))
    .with(P.nullish, () => ({
      label: 'No data',
      tone: 'neutral' as const,
    }))
    .exhaustive();

const VerdictBadge: FC<{
  verdict: PlatformRequirementDocumentVerdict | null | undefined;
}> = ({ verdict }) => {
  const { label, tone } = getRecordedVerdictBadge(verdict);
  const style = DOCUMENT_BADGE_TONE_STYLE[tone];

  return (
    <Badge
      bg={style.bg}
      border={style.border ? '1px solid' : undefined}
      borderColor={style.border}
      borderRadius="full"
      color={style.color}
      flexShrink={0}
    >
      {label}
    </Badge>
  );
};

/**
 * One identified contract document within an attached document.
 *
 * Shows the form as a chip and the category as plain text — never two competing
 * chips. Whichever half is missing is simply omitted; no "no form" / "no
 * category" placeholder, which is noise on a row whose point is the verdict.
 *
 * Matched sibling documents only ever carry the value that actually matched
 * (see `buildRecordedEvaluationDocuments`), so their rows are legitimately
 * chip-only or text-only. That falls out of the same rule.
 */
const ContractDocumentRow: FC<{
  contractDocument: ChecklistContractDocumentView;
  verdict: PlatformRequirementDocumentVerdict | null | undefined;
}> = ({ contractDocument, verdict }) => (
  <Flex align="center" gap={2} justify="space-between">
    <Flex align="center" gap={2} minW={0} wrap="wrap">
      {contractDocument.form ? (
        <ChecklistFormChip form={contractDocument.form} />
      ) : null}
      {contractDocument.documentCategory ? (
        <Text color="compliance.text.secondary" fontSize="sm">
          {formatCategory(contractDocument.documentCategory)}
        </Text>
      ) : null}
      {contractDocument.form == null &&
      contractDocument.documentCategory == null ? (
        <Text color="compliance.text.muted" fontSize="sm" fontStyle="italic">
          no classification
        </Text>
      ) : null}
    </Flex>
    <VerdictBadge verdict={verdict} />
  </Flex>
);

interface ChecklistIssueTooltipContentProps {
  checklistItemName: string;
  allowedDocumentCategories: string[];
  allowedForms: string[];
  evaluationDocuments: ChecklistGroupedEvaluationDocumentView[];
}

/**
 * Detail for a checklist issue: what the checklist item requires, and what each
 * evaluated attachment was actually classified as.
 *
 * Shared verbatim by the extension side panel and the agent compliance review
 * page. Their collapsed rows differ (each matches its own surface's validation
 * rows) but their inline evidence is the same, so this stays one component.
 *
 * "Documents checked" is not mismatch-only — the recorded evidence includes
 * matched siblings — so every row carries a verdict badge. An unbadged row in a
 * badged list reads as ambiguous rather than as passing.
 */
export const ChecklistIssueTooltipContent: FC<
  ChecklistIssueTooltipContentProps
> = ({
  checklistItemName,
  allowedDocumentCategories,
  allowedForms,
  evaluationDocuments,
}) => (
  <VStack align="stretch" spacing={3}>
    <Box>
      <Heading
        color="compliance.validation.tooltip.text.primary"
        mb={1}
        size="smallUppercase"
      >
        Checklist item
      </Heading>
      <Text fontSize="sm" fontWeight="semibold">
        {checklistItemName}
      </Text>
      <Box mt={1}>
        <ChecklistRequirementLine
          allowedDocumentCategories={allowedDocumentCategories}
          allowedForms={allowedForms}
        />
      </Box>
    </Box>

    {evaluationDocuments.length > 0 ? (
      <>
        <Divider
          aria-label="Documents checked separator"
          borderColor="compliance.tooltip.text"
          opacity={0.2}
        />
        <Box>
          <Heading
            color="compliance.validation.tooltip.text.primary"
            mb={2}
            size="smallUppercase"
          >
            Documents checked
          </Heading>
          <VStack align="stretch" spacing={3}>
            {evaluationDocuments.map((document) => (
              <Box key={document.renderKey}>
                <Text fontSize="sm" fontWeight="semibold">
                  {document.documentName}
                </Text>
                {document.contractDocuments.length === 0 ? (
                  // No identified contract documents, so there is no row to
                  // hang the badge on — keep it on the name so the verdict is
                  // never silently dropped.
                  <Flex align="center" gap={2} justify="space-between" mt={1}>
                    <Text
                      color="compliance.text.muted"
                      fontSize="sm"
                      fontStyle="italic"
                    >
                      no contract documents identified
                    </Text>
                    <VerdictBadge verdict={document.verdict} />
                  </Flex>
                ) : (
                  <VStack align="stretch" mt={1} spacing={1}>
                    {document.contractDocuments.map(
                      (contractDocument, index) => (
                        <ContractDocumentRow
                          key={`${document.renderKey}-cd-${index}`}
                          contractDocument={contractDocument}
                          verdict={document.verdict}
                        />
                      )
                    )}
                  </VStack>
                )}
              </Box>
            ))}
          </VStack>
        </Box>
      </>
    ) : null}
  </VStack>
);
